<script setup lang="ts">
/**
 * BO Notices — Stage 진입 전 설문 UI.
 * 1) Purpose(들) — identify / maintain 복수 선택 가능
 * 2) 각 목적별 통지 유형 다중 선택 → Continue
 */
import { computed, reactive, watch } from 'vue';
import {
  IDENTIFY_OPTIONS,
  MAINTAIN_OPTIONS,
  emptyBoNoticeSurvey,
  hasPurpose,
  isSurveyReady,
  normalizeSurvey,
  resolveIncludedDocs,
  type BoNoticeIdentifyOption,
  type BoNoticeMaintainOption,
  type BoNoticePurposeKind,
  type BoNoticeSurvey,
} from './boNoticeSurvey';
import { BO_NOTICE_DOCS } from './boNoticeFields';

const props = defineProps<{
  modelValue: BoNoticeSurvey;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', v: BoNoticeSurvey): void;
  (e: 'complete', v: BoNoticeSurvey): void;
}>();

const draft = reactive<BoNoticeSurvey>(normalizeSurvey(props.modelValue));

watch(
  () => props.modelValue,
  (v) => {
    const n = normalizeSurvey(v);
    draft.purposes = [...n.purposes];
    draft.identifyOptions = [...n.identifyOptions];
    draft.maintainOptions = [...n.maintainOptions];
    draft.completed = n.completed;
  },
  { deep: true },
);

function togglePurpose(p: BoNoticePurposeKind) {
  const i = draft.purposes.indexOf(p);
  if (i >= 0) {
    draft.purposes.splice(i, 1);
    if (p === 'identify') draft.identifyOptions = [];
    if (p === 'maintain') draft.maintainOptions = [];
  } else {
    draft.purposes.push(p);
  }
  sync();
}

function toggleIdentify(id: BoNoticeIdentifyOption) {
  const i = draft.identifyOptions.indexOf(id);
  if (i >= 0) draft.identifyOptions.splice(i, 1);
  else draft.identifyOptions.push(id);
  sync();
}

function toggleMaintain(id: BoNoticeMaintainOption) {
  const i = draft.maintainOptions.indexOf(id);
  if (i >= 0) draft.maintainOptions.splice(i, 1);
  else draft.maintainOptions.push(id);
  sync();
}

function snapshot(): BoNoticeSurvey {
  return {
    purposes: [...draft.purposes],
    identifyOptions: [...draft.identifyOptions],
    maintainOptions: [...draft.maintainOptions],
    completed: draft.completed,
  };
}

function sync() {
  emit('update:modelValue', snapshot());
}

const showIdentify = computed(() => hasPurpose(draft, 'identify'));
const showMaintain = computed(() => hasPurpose(draft, 'maintain'));
const ready = computed(() => isSurveyReady(draft));
const previewCodes = computed(() => resolveIncludedDocs({ ...snapshot(), completed: true }));

function continueToDocs() {
  if (!ready.value) return;
  const next: BoNoticeSurvey = { ...snapshot(), completed: true };
  emit('update:modelValue', next);
  emit('complete', next);
}
</script>

<template>
  <div class="bo-survey">
    <div class="sv-head">
      <div class="sv-kicker">Before you start</div>
      <h2 class="sv-title">Which BO notices do you need?</h2>
      <p class="sv-sub">
        You can select <b>both</b> purposes in one task. Then pick the notice types under each.
        Only selected documents will appear in the package.
      </p>
    </div>

    <!-- Q1: Purpose (multi) -->
    <section class="sv-block">
      <div class="sv-q">1. What is the purpose of this task? <span class="sv-hint">(select one or both)</span></div>
      <div class="sv-purpose">
        <button
          type="button"
          class="sv-purp"
          :class="{ on: showIdentify }"
          @click="togglePurpose('identify')"
        >
          <span class="sv-purp-check">{{ showIdentify ? '✓' : '' }}</span>
          <span class="sv-purp-ic">🔎</span>
          <span class="sv-purp-t">Initial identification</span>
          <span class="sv-purp-d">Find / confirm who the BO is (s.60C(1)–(3))</span>
        </button>
        <button
          type="button"
          class="sv-purp"
          :class="{ on: showMaintain }"
          @click="togglePurpose('maintain')"
        >
          <span class="sv-purp-check">{{ showMaintain ? '✓' : '' }}</span>
          <span class="sv-purp-ic">✏️</span>
          <span class="sv-purp-t">Particulars maintenance</span>
          <span class="sv-purp-d">Registered BO info — change or accuracy check (s.60C(5))</span>
        </button>
      </div>
    </section>

    <!-- Q2a: Identify options -->
    <section v-if="showIdentify" class="sv-block">
      <div class="sv-q">2a. Identification — who are you sending to? <span class="sv-hint">(select all that apply)</span></div>
      <div class="sv-opts">
        <button
          v-for="o in IDENTIFY_OPTIONS"
          :key="o.id"
          type="button"
          class="sv-opt"
          :class="{ on: draft.identifyOptions.includes(o.id as BoNoticeIdentifyOption) }"
          @click="toggleIdentify(o.id as BoNoticeIdentifyOption)"
        >
          <span class="sv-check">{{ draft.identifyOptions.includes(o.id as BoNoticeIdentifyOption) ? '✓' : '' }}</span>
          <span class="sv-opt-body">
            <span class="sv-opt-t">{{ o.label }}</span>
            <span class="sv-opt-d">{{ o.hint }}</span>
            <span class="sv-opt-doc">→ {{ BO_NOTICE_DOCS[o.docCode].label }}</span>
          </span>
        </button>
      </div>
    </section>

    <!-- Q2b: Maintain options -->
    <section v-if="showMaintain" class="sv-block">
      <div class="sv-q">{{ showIdentify ? '2b' : '2' }}. Maintenance — what do you need to ask? <span class="sv-hint">(select all that apply)</span></div>
      <div class="sv-opts">
        <button
          v-for="o in MAINTAIN_OPTIONS"
          :key="o.id"
          type="button"
          class="sv-opt"
          :class="{ on: draft.maintainOptions.includes(o.id as BoNoticeMaintainOption) }"
          @click="toggleMaintain(o.id as BoNoticeMaintainOption)"
        >
          <span class="sv-check">{{ draft.maintainOptions.includes(o.id as BoNoticeMaintainOption) ? '✓' : '' }}</span>
          <span class="sv-opt-body">
            <span class="sv-opt-t">{{ o.label }}</span>
            <span class="sv-opt-d">{{ o.hint }}</span>
            <span class="sv-opt-doc">→ {{ BO_NOTICE_DOCS[o.docCode].label }}</span>
          </span>
        </button>
      </div>
    </section>

    <!-- Preview -->
    <div v-if="previewCodes.length" class="sv-preview">
      <div class="sv-prev-label">Documents to include ({{ previewCodes.length }})</div>
      <ul>
        <li v-for="c in previewCodes" :key="c">
          <span class="ic">{{ BO_NOTICE_DOCS[c].icon }}</span>
          {{ BO_NOTICE_DOCS[c].label }}
        </li>
      </ul>
    </div>

    <div class="sv-actions">
      <button type="button" class="sv-go" :disabled="!ready" @click="continueToDocs">
        Continue to notices →
      </button>
    </div>
  </div>
</template>

<style scoped>
.bo-survey {
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  background: #fff;
  border: 1px solid #e6ebf3;
  border-radius: 14px;
  box-shadow: 0 6px 18px rgba(20, 30, 60, .06);
  padding: 22px 22px 18px;
  color: #1f2d4d;
}
.sv-head { margin-bottom: 18px; }
.sv-kicker {
  font-size: 11px; font-weight: 800; letter-spacing: .06em; text-transform: uppercase;
  color: #1b64da; margin-bottom: 6px;
}
.sv-title { margin: 0 0 6px; font-size: 18px; font-weight: 800; line-height: 1.3; }
.sv-sub { margin: 0; font-size: 13px; color: #6b778c; line-height: 1.45; }
.sv-sub b { color: #1f2d4d; }

.sv-block { margin-top: 18px; }
.sv-q { font-size: 13.5px; font-weight: 800; margin-bottom: 10px; }
.sv-hint { font-weight: 600; color: #8a97b5; font-size: 12px; }

.sv-purpose { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
@media (max-width: 720px) { .sv-purpose { grid-template-columns: 1fr; } }

.sv-purp {
  position: relative;
  display: flex; flex-direction: column; align-items: flex-start; gap: 4px;
  text-align: left; padding: 14px 14px 12px 14px;
  border: 1.5px solid #d9e2f0; border-radius: 12px; background: #f7f9fc;
  cursor: pointer; transition: border-color .15s, background .15s, box-shadow .15s;
}
.sv-purp:hover { background: #eef3fb; border-color: #b9d0ff; }
.sv-purp.on { background: #eaf1ff; border-color: #1b64da; box-shadow: 0 0 0 1px #1b64da inset; }
.sv-purp-check {
  position: absolute; top: 10px; right: 10px;
  width: 20px; height: 20px; border-radius: 6px; border: 1.5px solid #c5d0e3; background: #fff;
  color: #fff; font-weight: 900; font-size: 12px;
  display: inline-flex; align-items: center; justify-content: center;
}
.sv-purp.on .sv-purp-check { background: #1b64da; border-color: #1b64da; }
.sv-purp-ic { font-size: 18px; line-height: 1; margin-bottom: 2px; }
.sv-purp-t { font-size: 13.5px; font-weight: 800; color: #1f2d4d; }
.sv-purp-d { font-size: 11.5px; color: #6b778c; line-height: 1.35; }

.sv-opts { display: flex; flex-direction: column; gap: 8px; }
.sv-opt {
  display: flex; align-items: flex-start; gap: 10px; width: 100%;
  text-align: left; padding: 11px 12px;
  border: 1.5px solid #d9e2f0; border-radius: 11px; background: #fff;
  cursor: pointer; transition: border-color .15s, background .15s;
}
.sv-opt:hover { background: #f7f9fc; border-color: #b9d0ff; }
.sv-opt.on { background: #eaf1ff; border-color: #1b64da; }
.sv-check {
  flex: none; width: 22px; height: 22px; margin-top: 1px;
  border-radius: 6px; border: 1.5px solid #c5d0e3; background: #fff;
  color: #1b64da; font-weight: 900; font-size: 13px;
  display: inline-flex; align-items: center; justify-content: center;
}
.sv-opt.on .sv-check { background: #1b64da; border-color: #1b64da; color: #fff; }
.sv-opt-body { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.sv-opt-t { font-size: 13px; font-weight: 800; color: #1f2d4d; }
.sv-opt-d { font-size: 11.5px; color: #6b778c; line-height: 1.35; }
.sv-opt-doc { font-size: 11px; font-weight: 700; color: #1b64da; margin-top: 2px; }

.sv-preview {
  margin-top: 16px; padding: 12px 14px; border-radius: 10px;
  background: #f4f7fc; border: 1px dashed #c9d6ea;
}
.sv-prev-label { font-size: 11.5px; font-weight: 800; color: #5b6472; margin-bottom: 6px; }
.sv-preview ul { margin: 0; padding: 0; list-style: none; display: flex; flex-direction: column; gap: 4px; }
.sv-preview li { font-size: 12.5px; font-weight: 700; color: #1f2d4d; display: flex; align-items: center; gap: 6px; }
.sv-preview .ic { font-size: 13px; }

.sv-actions { display: flex; justify-content: flex-end; margin-top: 18px; }
.sv-go {
  border: none; border-radius: 10px; padding: 11px 18px;
  background: #1b64da; color: #fff; font-size: 13.5px; font-weight: 800; cursor: pointer;
}
.sv-go:hover:not(:disabled) { background: #1550b8; }
.sv-go:disabled { opacity: .45; cursor: default; }
</style>
